import { Box } from '@hover/blueprint';
import { iPhone } from '@hover/icons';
import { captureException } from '@sentry/react';
import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router';

import { config } from 'src/config';
import { Alert, Button } from 'src/shared/components';
import { Form as SharedForm, TextField } from 'src/shared/components/forms';
import { useEphemeralEmail, useTranslate } from 'src/shared/hooks';
import { TranslationNamespace } from 'src/shared/hooks/useTranslate';
import { Route } from 'src/shared/route';
import { request } from 'src/shared/utils/request';

import type { DownloadFormData } from './types';
import { makeValidationSchema } from './validationSchema';

const Form = () => {
  const t = useTranslate(TranslationNamespace.SIGN_UP);
  const { pathname } = useLocation();
  const history = useHistory();
  const { clear: clearEphemeralEmail } = useEphemeralEmail();

  const [loading, setLoading] = useState(false);
  const [sendHomeownerSmsError, setSendHomeownerSmsError] =
    useState<Error | null>(null);

  const initialValues: DownloadFormData = {
    mobilePhone: '',
  };

  const isSuccessRoute =
    pathname === Route.SIGN_UP_HOMEOWNER_DOWNLOAD_SUCCESS.path;

  const onSubmit = async (formData: DownloadFormData) => {
    try {
      setLoading(true);
      setSendHomeownerSmsError(null);

      await request(`${config.API_URI}/users/send_homeowner_sms`, {
        body: JSON.stringify({ number: formData.mobilePhone }),
        headers: { 'Content-Type': 'application/json' },
        method: 'POST',
      });

      clearEphemeralEmail();
      history.push(Route.SIGN_UP_HOMEOWNER_DOWNLOAD_SUCCESS.path);
    } catch (error) {
      // See DS-846
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      setSendHomeownerSmsError(error);
      captureException(error);
    } finally {
      setLoading(false);
    }
  };

  const validationSchema = makeValidationSchema(t);

  const hasErrorAtIdle = sendHomeownerSmsError && !loading;

  return (
    <SharedForm<DownloadFormData>
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
    >
      <Box display={isSuccessRoute ? 'none' : 'block'}>
        <TextField
          iconBefore={iPhone}
          label={t('homeownerDownload.phoneNumber')}
          name="mobilePhone"
          type="tel"
        />
      </Box>
      {hasErrorAtIdle && (
        <Alert alignSelf="stretch" flexDirection="row" marginBottom={300}>
          {t('somethingWentWrongTryAgain')}
        </Alert>
      )}
      <Button
        alignSelf="stretch"
        isLoading={loading}
        marginTop={400}
        size="large"
        testId="send-link-button"
        type="submit"
      >
        {t(
          isSuccessRoute
            ? 'homeownerDownload.sendTextLinkAgain'
            : 'homeownerDownload.getLink',
        )}
      </Button>
    </SharedForm>
  );
};

export { Form };
